// Read-only rows for the create page's Review step, built from Form's state.
// Pure, so it can be unit tested; nothing here feeds the create() payload.
import moment from 'moment';
import { childProjectNameParts, normalizeChildProjectName } from '../childProjectName';
import { statusLabel } from './surveyMetrics';

const DASH = '—';

const text = (v: any): string => (v === undefined || v === null || String(v).trim() === '' ? DASH : String(v));
const date = (v: any): string => (v && moment(v).isValid() ? moment(v).format('MM/DD/YYYY') : '');
const yes = (v: any): boolean => v === true || v === 'Y' || v === 'true';

export type ReviewRow = { label: string; value: string };

export function projectReviewRows(s: any): ReviewRow[] {
  const start = date(s.publishDate);
  const end = date(s.expiryDate);
  const devices = String(s.deviceCompatibility || '').split(',').map((d) => d.trim()).filter(Boolean);
  const validation = [
    s.validateStartEndIp === 'Y' ? 'Start-End IP' : '',
    s.validateCountry === 'Y' ? 'Country' : '',
  ].filter(Boolean);
  const excluded = (s.selectedSurveyOptionss || []).map((o: any) => o && o.label).filter(Boolean);
  return [
    { label: 'Project Name', value: text(s.projectName) },
    { label: 'Project ID', value: text(s.projectId) },
    { label: 'Client', value: text(s.client && s.client.label) },
    { label: 'PM Name', value: text(s.pmName) },
    // Description options are stored as "Survey On <topic>".
    { label: 'Description', value: text(String(s.description || '').replace(/^Survey On /, '')) },
    { label: 'Initial status', value: statusLabel(s.surveyType) },
    { label: 'IR', value: text(s.clientir) },
    { label: 'Survey Length', value: `${text(s.surveyLength)} min` },
    { label: 'Speeder Limit', value: `${text(s.outlierCutoffTime)} min` },
    { label: 'Field dates', value: start || end ? `${start || DASH} – ${end || DASH}` : DASH },
    { label: 'Cost Per Interview', value: `${text(s.costPerInterview)}${s.currency ? ` ${s.currency}` : ''}` },
    { label: 'Survey URL Identifier', value: text(s.surveyUrlIdentifier) },
    { label: 'Devices', value: devices.length ? devices.join(', ') : DASH },
    { label: 'Validation', value: validation.length ? validation.join(', ') : 'None' },
    { label: 'Exclude Survey Completes', value: excluded.length ? excluded.join(', ') : DASH },
  ];
}

export type MarketReviewRow = {
  key: string;
  market: string;
  country: string;
  childProject: string;
  quota: number | string;
  links: string;
  prescreen: string;
};

// markets: Form's market tabs ({ childKey, label, shortName, countryName }).
export function marketReviewRows(s: any, markets: any[]): MarketReviewRow[] {
  const parentName = s.projectName || '';
  return markets.map((m) => {
    const c = (s.childStates || {})[m.childKey] || {};
    const sn = m.shortName || '';
    const childProject = normalizeChildProjectName(c.childProjectName, parentName, sn)
      || childProjectNameParts(parentName, sn).base;
    const linkParts = yes(c.useUniqueLinks)
      ? ['Unique links']
      : [c.testurl ? 'Test URL' : '', c.url ? 'Live URL' : ''].filter(Boolean);
    const quota = c.userLimitCommitted;
    return {
      key: m.childKey,
      market: m.label,
      country: m.countryName,
      childProject,
      quota: quota === undefined || quota === null || quota === '' ? DASH : quota,
      links: linkParts.length ? linkParts.join(' + ') : 'Not set',
      prescreen: c.preQuestionsYN === 'Y' ? `Yes (${(c.selectedPreScreenQues || []).length})` : 'No',
    };
  });
}

export function totalQuota(s: any, markets: any[]): number {
  return markets.reduce((sum, m) => {
    const v = Number(((s.childStates || {})[m.childKey] || {}).userLimitCommitted);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);
}
